// Module 01 — domain (shared vocabulary). Implements ROADMAP §4.
// Frozen interface: docs/interfaces/domain.v3.md (+ additive v3.1/v3.2/v4 migration notes therein —
// λ, nuclearMw/exportMw, the 13-fuel taxonomy, BranchFlow analytics, Generator status/availabilityFactor,
// PriceSample delivery/locational, GridNode.ownerRole, FlowSample.kind)  ·  Importable by ALL modules
// (the only universal dependency; CLAUDE.md rule #2). Pure types + tiny pure
// helpers ONLY — no logic, no I/O, no DOM. Field names mirror the Debug 3 schema
// so export stays loss-free. v2 is a strict additive superset of v1 (see the
// migration note in domain.v2.md); v2 grows additively, breaking changes ⇒ domain.v3.

// ───────────────────────── Identifiers (branded strings) ─────────────────────
export type NodeId = string & { readonly __brand: "NodeId" };
export type EdgeId = string & { readonly __brand: "EdgeId" };
export type GenId = string & { readonly __brand: "GenId" };
export type BranchId = string & { readonly __brand: "BranchId" };

// Constructors: identity casts that brand raw id strings read from JSON. The only
// sanctioned helpers (domain.v2 §Helpers) — no validation, no side effects.
export const asNodeId = (s: string): NodeId => s as NodeId;
export const asEdgeId = (s: string): EdgeId => s as EdgeId;
export const asGenId = (s: string): GenId => s as GenId;
export const asBranchId = (s: string): BranchId => s as BranchId;

// ───────────────────────────────── Geometry ──────────────────────────────────
export interface Coord {
  lat: number;
  lon: number;
}
export type Polyline = readonly Coord[];

// ───────────────────── Voltage / classification enums (from Debug 3) ──────────
export type VTier = "EHV" | "HV" | "ST" | "MV" | "LV";
export type NodeClass =
  | "generator"
  | "substation"
  | "switch"
  | "transformer"
  | "junction"
  | "consumer"
  | "prosumer";
export type BusType = "PQ" | "PV" | "slack";
// Settlement-chain ownership role (economics overhaul, build log 47). DERIVED from the asset's tier/class
// — NOT a synthetic node: in Ontario the statutory 50 kV line maps onto the data we already carry, so
// EHV/HV/ST + transformer stations are TRANSMITTER-owned (Hydro One), MV/LV substations are LDC-owned
// (Toronto Hydro / Alectra). Used to route the 3 retail-bill legs (commodity→generator, transmission→
// transmitter, distribution→LDC) to the nearest upstream asset of each role. Accounting overlay only.
export type OwnerRole = "TRANSMITTER" | "LDC" | "GENERATOR" | "CONSUMER" | "JUNCTION";
// v4: widened to the REAL data taxonomy (data/reduced/generators.json carries 13 fuel strings;
// the prior 11-member union silently lied — e.g. GAS_PEAKER/GAS_OIL_STEAM/WASTE_TO_ENERGY were
// never representable, so `fuel === "EFW"` never matched real data). Legacy members (GAS, BIOGAS,
// EFW, OTHER) kept for back-compat with older fixtures/tests.
export type Fuel =
  | "NUCLEAR"
  | "HYDRO"
  | "GAS_CCGT"
  | "GAS_CHP"
  | "GAS_COGEN"
  | "GAS_PEAKER"
  | "GAS_OIL_STEAM"
  | "WIND"
  | "SOLAR"
  | "BATTERY"
  | "BIOGAS_LFG"
  | "BIOENERGY"
  | "WASTE_TO_ENERGY"
  | "GAS" // legacy
  | "BIOGAS" // legacy
  | "EFW" // legacy (energy-from-waste; real data uses WASTE_TO_ENERGY)
  | "OTHER";
// v4: the 6 dispatch roles + the network-role tags the reduced data actually carries. `networkRole`
// on Generator stays `string` (forward-compatible) but this names the known set.
export type NetworkRole =
  | "BASELOAD"
  | "DISTRIBUTED_BASELOAD"
  | "LOAD_FOLLOWING"
  | "PEAKER"
  | "VARIABLE"
  | "STORAGE";
export type DispatchRole =
  | "must_run_vre"
  | "baseload_flat"
  | "load_following"
  | "peaker"
  | "storage"
  | "reservoir_hydro"; // engine.v3 P10: energy-limited reservoir hydro — NOT pinned (rides the merit pool on water value)
export type GridRole = "gta_internal" | "import_transmission" | "external_embedded";

// ──────────────────── Nodes (post-reduction electrical nodes) ─────────────────
export interface GridNode {
  id: NodeId;
  cls: NodeClass;
  name?: string;
  coord: Coord;
  vTier: VTier;
  nominalKv: number;
  busType: BusType; // PQ unless PV/slack
  isTransformerStation?: boolean;
  operator?: string;
  degree: number; // in the REDUCED graph
  // settlement-chain ownership (build log 47, additive/optional) — derived in reduce.py from cls/vTier/
  // isTransformerStation/operator; consumed by the engine to route the 3 bill legs + by viz for picking.
  ownerRole?: OwnerRole;
  ownerName?: string; // "Hydro One" | "Toronto Hydro" | "Alectra" | … (illustrative where not from operator)
  // validated transformer-station attributes (build log 59, additive/optional) — researched typical or
  // named-station MVA + %Z + transformation class, set in reduce.py from electrical_standards.json.
  transformerStationMva?: number;
  transformation?: string; // e.g. "500/230", "230/27.6", "none"
  transformerZPct?: number;
  // V3-swap (build log 75, additive): superior fields ADOPTED from the V3 transmission model on matched/
  // added facilities (no-downgrade enrichment) — the connection voltage, the station-MVA basis (published
  // vs class default), the OSM hub id this node merged from, and honest provenance labels. Datasheet-facing.
  connectionKv?: number;
  stationMvaBasis?: string;
  osmHub?: string;
  attrSource?: string;
  attrConfidence?: string;
  // V4 §3.5(c) (additive): the OSM id of this facility's footprint parcel polygon. The SEPARATE pickable
  // parcels layer joins parcel→node by this id, so a node↔parcel selection round-trips both ways.
  parcelId?: string;
  // ONT full-integration (additive): a real transmission station with no electrical path to generation
  // (after gap-bridging). Kept in the model + RENDERED as static infrastructure, but the engine excludes
  // it from the DC solve + the outage count (it is context, not a blackout).
  displayOnly?: boolean;
  // engine.v2 realistic-involvement (Core-0, additive/optional) — full AC bus electrical state. UNSET in
  // the DC-default tick path (the golden driver carries only θ); SET by the LinDistFlow distribution pass
  // (vMagPu at delivery points) and the optional AC Newton-Raphson solve. Consumers treat undefined as
  // "engine ran DC-only this tick". See docs/interfaces/domain.v3.md v4.x note.
  // engine.v3 P7 (C18 PROMOTED): this field is no longer dead — the per-bus voltage magnitude is now
  // PRODUCED every tick on the Snapshot as `busVMagPu` (√w from the LinDistFlow proxy, no AC solve). This
  // GridNode field remains the per-node carrier for a future AC/fitted pass; the live value ships on Snapshot.
  vMagPu?: number; // solved voltage magnitude (pu); ~1.0 at the slack
  qInjMvar?: number; // net reactive power injection at the bus (Mvar)
  gsMw?: number; // bus shunt conductance, as MW drawn at 1 pu (Ybus diagonal real part · Sbase)
  bsMvar?: number; // bus shunt susceptance, as Mvar at 1 pu (+ = capacitor, − = reactor)
}

// ─────────────── Edges (reduced; carry both physics and display geometry) ─────
export type EdgeKind =
  | "line"
  | "distribution_feeder"
  | "neighbour_tie"
  | "gen_interconnect"
  | "merged";
export interface GridEdge {
  id: EdgeId;
  u: NodeId;
  v: NodeId;
  kind: EdgeKind;
  vTier: VTier;
  kv: number;
  rPu: number; // series merge: r = Σr
  xPu: number; //              x = Σx
  bPu: number; //              b = Σb
  rateMva: number; // = min over merged segments
  lengthKm: number; // = Σ length
  circuits: number;
  polyline: Polyline; // concatenated true geometry
  origBranchIds: readonly BranchId[]; // provenance → Debug 3 branches subsumed
  isTie?: boolean; // normally-open tie (FLISR uses these)
  isSwitchable?: boolean; // can be opened/closed by FLISR/user
  // validated line attributes (build log 59, additive/optional) — researched per-voltage-class conductor
  // + thermal MVA rating; voltageInferred flags an edge whose kV was flooded (not OSM-tagged).
  conductor?: string;
  thermalMva?: number;
  voltageInferred?: boolean;
  // ONT full-integration (additive): a display-only corridor (touches a display-only station, excluded
  // from the solve) / an inferred bridge that closed an OSM gap to connect a disconnected ONT component.
  displayOnly?: boolean;
  // V4 P-FIX (2026-06-28, additive): a TRANSFORMER winding (engine treats it as a plain xPu branch, kind stays
  // 'line'); the renderer draws it SOLID, never as a marching arrow (an animated chevron on a ~0-length winding
  // reads as a wrong-direction arrow on the backbone). See viz adapter energyGlyphActive + the F/R material gate.
  isTransformerLeg?: boolean;
  inferredBridge?: boolean;
  // P1 reality fix (additive): honest provenance for OSM-unmapped-but-REAL connectors, superseding the
  // blanket `inferredBridge`. `serviceConnection` = a distribution TS feeding its co-located neighbourhood
  // (a real service drop OSM never maps as a line); `undergroundInferred` = a real (downtown urban) cable
  // interconnect OSM never mapped; `spurConnector` = a real station's second tie OSM left one-ended;
  // `realCorridor` = a research-confirmed named corridor carrying corridorRef/Source/Confidence. All carry
  // realistic per-voltage-class impedance (electrical_standards.json), not the old synthetic constants.
  serviceConnection?: boolean;
  undergroundInferred?: boolean;
  spurConnector?: boolean;
  realCorridor?: boolean;
  corridorRef?: string;
  corridorSource?: string;
  corridorConfidence?: string;
  // V3-swap (build log 75, additive): superior fields ADOPTED from the V3 transmission model — the physical
  // cable count, voltage provenance (osm|inferred|research|default|corrected-*), the per-END breaker state
  // (uClosed/vClosed — the V3 connections_map per-connection switch; carried as DATA here, wired to the
  // engine in the per-port-breaker phase), and a provenance label. Datasheet-facing.
  cables?: number;
  voltageProvenance?: string;
  uClosed?: boolean;
  vClosed?: boolean;
  attrSource?: string;
  // V3-swap (build log 75, P6 + REV-A): a superseded legacy CORE (Debug-3) transmission line — the V3
  // backbone now represents this corridor, so this edge is RETIRED: marked isTie (the engine opens it ⇒ out
  // of B'/energization) AND supersededLegacy (the renderer HIDES it from the default map). Kept in data (no
  // deletion; gate anchor) + behind a legacy toggle. Distinct from a real normally-open tie.
  supersededLegacy?: boolean;
  // engine.v2 realistic-involvement (Core-0, additive/optional) — per-circuit identity. When a real
  // multi-circuit corridor is emitted as separate sibling branches (Core-A, replacing the impedance-merge),
  // every sibling shares one `corridorId` (= the base corridor edge id) and carries its 1-based
  // `circuitIndex`. The renderer groups siblings by corridorId (each circuit draws its own polyline; an
  // N-1 trip darkens one circuit while the sibling brightens). Absent on a single-circuit edge.
  corridorId?: EdgeId;
  circuitIndex?: number;
  // Phase-0 (ontario-merge): impedance heterogeneity within a corridor = max(xPu)/min(xPu) over its
  // siblings. 1.0 ⇒ identical circuits (the equal-split N-1 closed-form is EXACT); >1 ⇒ heterogeneous
  // (the closed-form is an ESTIMATE; the exact inverse-impedance LODF is Phase 4). Set only on siblings.
  maxXpuRatioSiblings?: number;
  // Phase-0 (ontario-merge): this sibling shares its physical route (polyline) with another sibling ⇒ they
  // render COINCIDENT, so the viz fans them apart. False ⇒ a genuinely different-route parallel that already
  // draws distinctly and must stay on its true OSM path (no fan). Set only on tagged/split siblings.
  coincident?: boolean;
  // Operating domain (Core-E.4): "mesh" = normally-closed bulk transmission (≥115 kV, all parallels carry
  // simultaneously, N-1 re-divides by impedance); "radial" = distribution operated as a spanning tree with
  // normally-open ties. Drives the two-domain solve. Absent ⇒ inferred from kind/vTier.
  operatingDomain?: "mesh" | "radial";
  // A normally-open redundant feed kept for FLISR restoration — distinct from `isTie` (a pre-existing
  // neighbour tie): set on a load's FORMER distribution feed retained after re-homing to the backbone
  // (Core-B), so the radial solver leaves it open until a fault opens the primary path.
  normallyOpen?: boolean;
  // Transformer two-port off-nominal ratio (Core-E.1 Ybus): |tap| magnitude (1.0 = nominal) + phase-shift
  // angle (deg). Set only on transformer/gen_interconnect edges crossing a voltage class; absent on lines.
  tapRatio?: number;
  shiftDeg?: number;
}

// ───────────────────────────────── Generators ────────────────────────────────
export type GeneratorStatus = "Operating" | "Under_Construction" | "Retired" | "Decommissioned";

export interface Generator {
  id: GenId;
  busId: NodeId; // attaching bus
  name: string;
  fuel: Fuel;
  capMw: number;
  costPerMwh: number; // merit-order key
  gridRole: GridRole;
  mustRun?: boolean; // nuclear baseload
  coord: Coord; // TRUE site (Debug 3 restored coords)
  // ── v3 restored Debug 3 dispatch-dynamics attributes (all optional — additive widening) ──
  status?: GeneratorStatus; // 64 Operating · 17 Under_Construction · 3 Retired · 1 Decommissioned
  minOutputMw?: number; // Pmin (unit floor when committed)
  rampRateMwPerMin?: number;
  // ── engine.v3 P9 temporal-dynamics: unit-commitment parameters (all optional, additive) ──
  // Absent ⇒ the per-fuel fallback table in src/engine/commitment.ts applies (or, truly rateless, a neutral
  // pass-through) ⇒ pre-P9 behaviour, byte-identical on the frozen fixture. minUp/minDownTimeH are integer-hour
  // lockout counters; startup/noLoad are $ (one-time on commit / $-per-hour while committed regardless of output).
  startupCost?: number;   // $ one-time cost on the decommitted → committed transition
  noLoadCost?: number;    // $/h charged while committed regardless of output (fuel-to-idle + fixed O&M)
  minUpTimeH?: number;    // hours the unit must stay committed once started
  minDownTimeH?: number;  // hours the unit must stay decommitted once shut
  // ── engine.v3 P10 hydro reservoir + water-value dispatch (all optional, additive; domain.v7 P10 sub-block) ──
  // Present ⇒ this is an ENERGY-LIMITED reservoir hydro unit that self-arbitrages against λ via a water value
  // (marginal opportunity cost of stored water: base + slope·(1−soc/cap), rising as the reservoir draws down).
  // Absent ⇒ NOT a reservoir unit ⇒ pre-P10 behaviour (Run-of-River hydro stays a flat baseload_flat floor),
  // byte-identical on the frozen fixture (whose gens carry no hydro attrs). Derived code-only at model load
  // (ts-engine.init) from capMw · reservoir-hours — data/reduced is NOT touched.
  reservoirEnergyMwh?: number;      // reservoir capacity (MWh-equiv); > 0 ⇒ classifier routes it to reservoir_hydro
  inflowMw?: number;                // continuous inflow / run-of-river must-take floor (MW); absent ⇒ 0
  waterValueBasePerMwh?: number;    // water value at a FULL reservoir ($/MWh; ≈ the legacy hydro cost floor)
  waterValueSlopePerMwh?: number;   // extra $/MWh at an EMPTY reservoir (scarcity premium); WV = base + slope·(1−soc/cap)
  hydroMaxDischargeMw?: number;     // nameplate max discharge (default capMw); the RoR must-take min is `inflowMw`
  // engine.v3 P10-rewrite (2026-07-04) — the PIECEWISE-LINEAR water-value ladder (D2) + the head effect (D3).
  // Absent ⇒ the default 4-rung convex-decreasing ladder (calibrated from base+slope) + headMinFactor 0.85.
  // Present ⇒ an explicit PWL scarcity curve (the derivative of the convex future-cost function SDDP produces) +
  // a per-unit head floor. Both additive-optional; absent on the golden fixture ⇒ byte-identical.
  waterValueBreakpoints?: readonly number[];  // D2: fill fractions [0..1] for the PWL water-value ladder (≥ 2, sorted)
  waterValueValues?: readonly number[];       // D2: $/MWh at each breakpoint (convex-decreasing: rises as frac falls)
  headMinFactor?: number;                      // D3: head factor at an EMPTY reservoir (∈ [0,1]; default 0.85)
  storageEnergyMwh?: number; // BATTERY fleet only — energy reservoir
  storageDurationH?: number; // BATTERY fleet only
  // domain.v5 (engine.v3 P4, C34): explicit BATTERY power ratings = the real C-rate (storageEnergyMwh /
  // durationH), decoupling MW from MWh. == capMw on the current nameplate-rated fleet; the grid-BESS
  // charge path reads chargeMw ?? capMw so a rating-less unit (golden/legacy) is byte-identical.
  chargeMw?: number;
  dischargeMw?: number;
  roundTripEfficiency?: number; // 0..1 (BATTERY)
  heatRateBtuPerKwh?: number; // thermal units
  // engine.v3 audit fix (2026-07-03): the PRICE-INDEPENDENT carbon adder embedded in costPerMwh, anchored on
  // the record by the V3/v4 gen-params stamp so a re-run at a NON-baseline fuel price reuses it instead of
  // re-deriving it from the already-shifted costPerMwh (which would compound heatRate·Δprice each run). Absent
  // on the shipped fleet (the stamp isn't re-run this pass) ⇒ engine-neutral; the engine never reads it (it's a
  // tooling anchor for idempotent re-stamping). See V3/v4/gen-params.mjs deriveCost.
  carbonAdderPerMwh?: number;
  co2KgPerMwh?: number;
  capacityFactor?: number; // 0..1 historical
  // v4 (reality audit): per-UNIT availability derate on top of the fuel availFactor — models a
  // partial-fleet outage (e.g. Bruce A with one of four units in mid-life refurbishment). availMw =
  // capMw · availFactor(fuel,env) · (availabilityFactor ?? 1). Optional; default 1 (fully available).
  availabilityFactor?: number;
  networkRole?: string; // BASELOAD | PEAKER | VARIABLE | STORAGE | LOAD_FOLLOWING | …
  meritRank?: number; // Debug 3 dispatch_priority/merit ordering
  isChp?: boolean;
  blackStartCapable?: boolean;
  fuelSubtype?: string;
  operator?: string;
  iesoZone?: string;
  // ── v4 (P11 pricing): distance + transmission-loss-adjusted DELIVERED cost + intertie corridor.
  // ALL optional + additive (domain.v3 → v4). Absent ⇒ merit falls back to bare costPerMwh and the
  // scalar import cap, so legacy fixtures (the frozen golden) stay byte-identical; these populate on
  // the LIVE model only after the V3/v4 data chain computes them (the P11 RE-BASELINE swap).
  distanceFromGtaKm?: number; // great-circle km from the GTA load centre to the unit's site
  deliveredCostPerMwh?: number; // costPerMwh / lossFactor, lossFactor = 1 − 0.02·(km/100) (IESO 0.91–1.22 span)
  // The GTA-directional import corridor this out-of-GTA unit reaches the load centre through — one of the
  // SIX real values the V4 data uses (see CorridorId; NOT the 5 Ontario external interties — corrected P5;
  // the earlier comment drifted). engine.v3 P5 keys per-corridor import ATC (data/intertie_caps.json) on
  // this value; absent ⇒ the merit falls back to the pooled interface cap. Typed `string` (not CorridorId)
  // since the V4 data emits it unvalidated; the engine treats an unknown value as "uncapped for this unit".
  importCorridor?: string;
}

// engine.v3 P5 — the six GTA-directional bulk-transmission import corridors the rest of Ontario feeds the
// GTA through (Generator.importCorridor values). Homed in domain (the shared vocabulary) so powerflow's
// SCED + the Snapshot totals both reference one definition without a cross-module import (CLAUDE.md #2).
export type CorridorId = "BRUCE" | "EAST" | "NIAGARA" | "SOUTHWEST" | "OTTAWA" | "WEST";

// ─────────────────────── Loads (the 449 neighbourhoods) ───────────────────────
// v3: per-neighbourhood demand model restored from Debug 3 (the upstream computes
// D(t) = Dmax · seasonalScaling(season) · [base + (1−base)·shape24h(hour)] per load —
// these are the attributes that make time-of-day and temperature act on EACH node).
export interface SeasonalScaling {
  winter: number;
  spring: number;
  summer: number;
  fall: number;
}
// 24 hourly multipliers (0..1, peak hour = 1.0), one vector per (season, dayType) pair.
export interface DemandProfiles24h {
  summerWeekday: readonly number[];
  summerWeekend: readonly number[];
  winterWeekday: readonly number[];
  winterWeekend: readonly number[];
}

export interface Load {
  id: NodeId; // e.g. "TOR_TOR_174"
  name: string;
  kind: "consumer" | "prosumer";
  busId: NodeId; // grid attachment
  maxDemandMw: number; // Dmax
  criticalityPriority: number; // 1 = highest (restoration order)
  developmentDensity: number; // 0..1
  loopType?: string; // tier: A_DOWNTOWN_MESH | B_URBAN_OPEN_LOOP | C_SUBURBAN_RADIAL | D_RURAL_RADIAL
  coord: Coord;
  areaPolygon?: Polyline; // neighbourhood_areas.geojson (extruded "wall" footprint)
  // prosumer-only (static nameplate; runtime state lives in ProsumerRuntime):
  peakSolarMw?: number;
  batteryCapacityMwh?: number;
  batteryChemistry?: string; // e.g. "LFP"
  // ── v3 restored Debug 3 attributes (all optional — additive widening) ──
  demandProfiles?: DemandProfiles24h; // per-load 24h shapes (season × dayType)
  peakHourSummer?: number; // 0..23 — hour where the summer shape = 1.0
  peakHourWinter?: number;
  seasonalScaling?: SeasonalScaling; // multipliers on Dmax by season
  weatherSensSummerMwPerDegC?: number; // ΔMW per °C above summer reference
  weatherSensWinterMwPerDegC?: number; // ΔMW per °C below winter reference
  annualConsumptionGwh?: number;
  loadFactorPct?: number;
  // prosumer economics/physics (Debug 3 market + battery model):
  rooftopSuitabilityScore?: number; // 0..1 — PV suitability derate
  batteryEtaAcAc?: number; // round-trip AC-AC efficiency (LFP ≈ 0.90, NMC ≈ 0.92)
  sellOfferPriceCkWh?: number; // prosumer export offer (¢/kWh) — drives the local market
  // demographics (inspect-panel realism; never solver inputs):
  population?: number;
  populationDensityPerKm2?: number;
  areaKm2?: number;
  // engine.v3 P11 (C31): distribution / last-mile loss coefficient — derived offline (developmentDensity-based)
  // and stamped at build time. Absent ⇒ the distribution-loss path skips to 0 (golden-neutral). Read-only model data.
  distLossCoef?: { constPct: number; quadCoef: number; peakMw: number };
  // engine.v3 P14 (C113): per-site reserve-floor override for the prosumer battery. Absent ⇒ the global
  // pricing.reserveFloorFrac (0.2). Site-specific because backup-power / criticality needs differ.
  reserveFloorFrac?: number;
  // engine.v3 P14 (C60): commodity-leg price source. "rpp" (default) = the OEB-set regulated retail
  // commodity (GA-bundled) — correct for Ontario RPP residential consumers, who do NOT see nodal LMP.
  // "nodal" = price the commodity leg on the dispatched λ (post-MRP wholesale signal) for flagged
  // non-RPP / wholesale-class loads; falls back to retailPerKwh when λ==0. Absent ⇒ "rpp" ⇒ golden-neutral.
  commodityPriceMode?: "rpp" | "nodal";
  // engine.v3 P15 (L4): the fraction of THIS consumer's demand that participates in the retail-TOU load-shift
  // (defer on-peak → release off-peak). Absent ⇒ 0 ⇒ the load does not shift ⇒ golden-neutral. Residential
  // ~0.05-0.06 (Brattle/Faruqui Ontario σ≈−0.1…−0.15; only a shiftable subset responds to TOU).
  shiftableFrac?: number;
}

// ─────────────── Time / scenario / environment / price ────────────────────────
export type Season = "winter" | "spring" | "summer" | "fall";
export type DayType = "weekday" | "weekend";
export interface Scenario {
  // the 5 Debug 3 anchors are instances of this
  id: string;
  label: string;
  hour: number; // 0..23 (HE)
  season: Season;
  dayType: DayType;
  tempC: number;
  // engine.v3 P15 (C115): statutory holiday flag. Absent ⇒ false ⇒ weekday/weekend shaping unchanged.
  // When true, the retail TOU resolver matches HOLIDAY-window tokens (price.ts) — a weekend-shaped price day.
  isHoliday?: boolean;
}
export interface EnvSample {
  scenario: Scenario;
  loadMult: (load: Load) => number; // diurnal × seasonal × temperature
  solarCF: number; // 0..1 capacity factor
  windCF: number; // 0..1
  // ── engine.v3 P8 env→infrastructure coupling (additive; default 1.0 = neutral) ──
  // A temperature now moves the INFRASTRUCTURE, not only demand. All three are deterministic pure fns of
  // s.tempC (from src/environment/derates.ts), populated by envSample(). Every consumer reads with `?? 1`,
  // so an old caller / old fixture (no field) is byte-identical (Liskov-safe widening).
  lineRatingFactor?: number;   // IEEE-738 DLR multiplier on rateMva (1.0 at/below 25°C; shrinks in heat)
  resistanceFactor?: number;   // ACSR R(T) multiplier on rPu       (1.0 at/below 25°C; climbs in heat)
  genDerateFactor?: (fuel: Fuel) => number; // GT ambient derate on availMw (1.0 at/below ISO 15°C, gas-CT only)
}
export interface PriceSample {
  tier: "ULO" | "TOU" | "TIERED";
  retailPerKwh: number; // $/kWh consumer COMMODITY price now (RPP supply; GA recovered, no adder)
  prosumerSellPerKwh: number; // export/sell price now (net-metering = commodity, 1:1)
  // economics overhaul (build log 45) — delivery + locational decomposition, all $/kWh. ADDITIVE +
  // OPTIONAL (existing PriceSample constructors / test fixtures need none). Filled by priceAt from
  // pricing.json `delivery`. These legs are PAID to the transmitter / LDC / IESO on top of the
  // commodity paid to generators; locationalCreditPerKwh is the value a LOCAL/peer kWh avoids.
  transmissionPerKwh?: number; // delivery: transmission (RTSR/UTR) component
  distributionPerKwh?: number; // delivery: effective distribution volumetric proxy (mostly fixed IRL)
  wmsPerKwh?: number; // regulatory: wholesale market service
  lineLossFactor?: number; // T&D loss gross-up (~1.04)
  locationalCreditPerKwh?: number; // avoidable value of a local/peer kWh = transmission + wms + retail·(llf−1)
  asOf: string; // source date of the tariff table
}

// ─────────────────────── Simulation results & flows ──────────────────────────
export type Layer = "energy" | "information" | "finance";
export interface Dispatch {
  genId: GenId;
  outputMw: number; // plant active iff outputMw>0; production capacity = Generator.capMw
  // P4 (ontario-merge) ADDITIVE: the generator's attaching bus, so the UI can offer a plant Shut-down/Restart
  // control on a generator pick (mapped to toggleNode(busId)) — out-of-GTA plants are at dedicated G_*_SITE
  // buses (no co-located load), so toggling the bus cleanly drops just that unit. Optional; older snapshots omit.
  busId?: NodeId;
  // engine.v2 ADDITIVE (System-Dynamics): turbine MECHANICAL power stock Pₘ (MW), governor-lagged.
  // At steady state Pₘ == outputMw; during a frequency transient Pₘ lags the droop demand by τ_g.
  // Optional — older snapshots / the v1 engine omit it.
  mechPowerMw?: number;
}
export interface BranchFlow {
  edgeId: EdgeId;
  flowMw: number;
  loadingPct: number; // ⇒ edge "pressure"/crisis viz
  // v4 ADDITIVE (engine-driven flow display): 1 ⇔ the edge is on a LIVE path (both endpoints in an
  // energized island) AND carrying real power this tick (|flowMw| > FLOW_EPS); 0 ⇔ de-energized,
  // idle (e.g. a normally-open tie carrying ~0), or faulted. The viz animates marching chevron
  // arrows ONLY when energized=1; direction is sign(flowMw). Optional: older snapshots omit it.
  energized?: 0 | 1;
  // v5 ADDITIVE (in-service display): 1 ⇔ the edge is IN SERVICE — both endpoints energized AND the edge is
  // closed (not a tie/fault/manual-out/cascaded-open) — REGARDLESS of whether it carries flow this tick. The
  // viz draws an in-service line SOLID in its tier colour (a real, live circuit): inService=1 & energized=1 ⇒
  // solid + marching arrows; inService=1 & energized=0 ⇒ solid, NO arrows (idle parallel / switchyard busbar);
  // inService=0 ⇒ faint (de-energized / out of service). Lets idle backbone read as live, not "off". Optional.
  inService?: 0 | 1;
  // v4 ADDITIVE (renderer contract, design §7) — the marching-arrow renderer consumes ONLY these:
  flowMag?: number; // |flowMw| / rateMva, clamped 0..1 → arrow speed/size/spacing
  flowDir?: 1 | -1 | 0; // sign(flowMw); 0 ⇒ no marching even if energized
  islandId?: number; // connected-component id (cascade legibility; -1 if de-energized)
  deEnergizedAtTick?: number; // tick this edge last went 1→0; renderer fades arrows over ~0.4 s
  // engine.v2 realistic-involvement (Core-0, additive/optional) — sibling-circuit identity mirrored onto
  // the flow so the renderer/datasheet can group parallel circuits without re-joining to GridEdge.
  corridorId?: EdgeId;
  circuitIndex?: number;
  // Worst single-contingency loading for this branch from the N-1 LODF screen (Core-E.2): the highest
  // post-outage |flow|/rate over all screened single-branch trips, as a fraction (1.0 = at rating).
  // Drives the "N-1 insecure" overlay. Absent ⇒ screening not run this tick.
  contingencyLoadingPct?: number;
  // Reactive branch flow (Mvar) from the LinDistFlow / AC solve (Core-E.1/.3); absent in the DC path.
  flowMvar?: number;
  // engine.v3 P12 (C22): protection-coordination state — 0 normal · 1 timing/picked-up · 2 tripped · 3 locked-out.
  // Absent on older snapshots ⇒ every reader gates with `?? 0`.
  protectionState?: 0 | 1 | 2 | 3;
}
export interface FlowSample {
  // for animated flow viz, per layer
  edgeId: EdgeId;
  layer: Layer;
  magnitude: number; // MW | packets/s | $/s
  direction: 1 | -1; // along edge u→v (1) or v→u (-1)
  // settlement leg (build log 47, additive/optional) — set on FINANCE flows so the viz renders the three
  // legs as distinct arcs: commodity→generator, transmission→transmitter, distribution→LDC; p2p→neighbour.
  kind?: "commodity" | "transmission" | "distribution" | "p2p";
}
export interface Fault {
  // NEW in v2: "comms" lets a cyberattack disrupt the information layer.
  targetKind: "branch" | "bus" | "comms";
  targetId: string;
}

// ───────── Per-entity runtime — NEW in v2 (optional Snapshot detail) ───────────
// Per-prosumer runtime state. Drives the battery-charge + production visuals and
// the export/serve flows. Computed by economics (05), assembled by the engine (07).
export interface ProsumerRuntime {
  id: NodeId; // the prosumer Load.id
  socFrac: number; // battery state of charge, 0..1 (typed view of the packed SoC array)
  solarMw: number; // current PV production, ≥0
  batteryMw: number; // signed battery power: >0 discharging (to load/grid), <0 charging
  servedMw: number; // own-neighbourhood load served behind-the-meter, ≥0
  exportMw: number; // surplus exported to neighbours via ties, ≥0
  // engine.v3 P14 (C58): the round-trip AC-AC η loss surfaced for BILLING. Energy that left the grid to
  // fill the cells but never reached load (dischargeMw·(1/√η − 1)·TICK_HOURS on the discharge path). η
  // default 1 ⇒ 0 ⇒ golden-neutral. P1 owns the net-load (energy) side; P14 owns the $ (bill) side.
  roundTripLossMwh?: number;
}
// Per-load service state. Drives the parcel "wall" colour grammar.
export type LoadServiceStatus =
  | "normal" // servedMw ≈ demandMw, feeding area healthy
  | "stressed" // served but feeder/area near limit (brownout-risk; "under pressure")
  | "shed" // deliberately curtailed by FLISR/operator (servedMw < demandMw, controlled)
  | "dark"; // de-energized (servedMw = 0; blackout)
export interface LoadRuntime {
  id: NodeId; // the Load.id
  demandMw: number; // current demand after env multiplier, ≥0
  servedMw: number; // actually delivered, 0..demandMw
  status: LoadServiceStatus;
}

// ──────────── Snapshot (worker → state, immutable, one per tick) ──────────────
// P13 (KS-PRICE legibility) ADDITIVE: the per-tick merit/supply stack — every dispatched unit ordered by
// DELIVERED cost so a view can render the cost-vs-cumulative-MW supply curve + name the price-setter. Carries
// the unit's delivered cost (P11: bare cost grossed up by transmission loss), its MW this tick, whether it set
// λ (isMarginal), and whether it is an import. Optional on the snapshot; older snapshots omit it.
export interface MeritStackEntry {
  genId: GenId;
  name: string;
  fuel: Fuel;
  deliveredCostPerMwh: number;
  mwDispatched: number;
  isMarginal: boolean;
  isImport: boolean;
}

// domain-v5 ADDITIVE (engine.v3 P2 — the always-on conservation harness). The per-tick physics
// self-check report, HOMED IN DOMAIN so it rides the wire (totals.invariant) without the worker
// importing powerflow. JSON-cloneable ONLY (plain readonly arrays/primitives — never a Map) so it
// survives structured-clone through worker/protocol.ts's packer-by-exclusion.
export interface InvariantViolation {
  /** which assertion family breached. */
  check: "conservation" | "islandBalance" | "hubKvl" | "capacity" | "stocks" | "money";
  /** human-readable: WHICH island/branch/bus/stock breached + the residual. */
  detail: string;
  /** signed magnitude of the breach (0 ⇒ pass). MW for physics checks, $ for money, Hz for freq. */
  residual: number;
}
export interface InvariantReport {
  /** no violation over tolerance this tick. */
  ok: boolean;
  /** −Σ_{i≠slack} P[i]·SBASE — the derived slack the DC solve absorbed (≠ dispatch slackMw). */
  slackAbsorbedMw: number;
  /** max |residual| over the PHYSICAL power-balance residuals only — global conservation, per-island KCL, and
   *  sample-hub KCL (the one "system imbalance" scalar a dashboard can plot). F6.4: the labelled decomposition
   *  cross-check and the SD-B3 over-gen margin are EXCLUDED (they are aggregation drift / a capacity margin, not
   *  KCL, and were O(GW) on shed islands — they still surface in `violations[]`, just not in this scalar). */
  worstResidualMw: number;
  /** empty ⇔ ok. */
  violations: readonly InvariantViolation[];
  /** DIAGNOSTIC (not a violation): closed branches over 100% continuous rating this tick. The live
   * N-0 grid carries ~20 such branches (max ~152%, weak-corridor DATA issue — measured 2026-07-02);
   * the DC engine deliberately tolerates loading between 1.0× and the 1.3× emergency threshold
   * (contingency.ts THERMAL_TRIP_FACTOR), so over-rating is surfaced here for PD/P3/P5 to attack,
   * not asserted fatal. TODO(P5-SCED): promote to a hard violation once limits genuinely bind. */
  capacityBreachCount: number;
  /** DIAGNOSTIC companion: the worst closed-branch loadingPct seen this tick (0 when none). */
  worstBranchLoadingPct: number;
}

export interface Snapshot {
  tick: number;
  scenario: Scenario;
  price: PriceSample;
  dispatch: readonly Dispatch[];
  branchFlows: readonly BranchFlow[];
  nodeAngles: ReadonlyMap<NodeId, number>; // θ on retained buses
  // domain-v7 ADDITIVE (engine.v3 P7 — LinDistFlow voltage): per-bus voltage magnitude vMagPu = √w (w=|V|²),
  // ~1.0 pu at the slack/nominal, 0 on a blacked-out bus. Always emitted (like nodeAngles) — the C18-promoted
  // GridNode.vMagPu counterpart, produced every tick from one matrix-vector product (no AC solve).
  busVMagPu: ReadonlyMap<NodeId, number>;
  busAngleMaxDeg: number;
  branchAngleMaxDeg: number;
  totals: {
    grossDemandMw: number;
    netLoadMw: number;
    genMw: number;
    slackMw: number;
    importMw: number;
    gtaGasMw: number;
    curtailedMw: number;
    unservedMw: number;
    reserveMw: number;
    // v3.1 ADDITIVE (dispatch-realism): system marginal price λ — costPerMwh of the most
    // expensive dispatched pool unit; 0 in surplus hours. Optional: older snapshots omit it.
    // engine.v3 F2: under an involuntary shortfall λ is lifted toward VOLL by scarcityAdderPerMwh.
    marginalCostPerMwh?: number;
    // engine.v3 F2 (RC-3) ADDITIVE: the scarcity adder ($/MWh) folded into λ during involuntary shed —
    // the delta over the merit clearing price. Absent/0 when the fleet serves demand ⇒ byte-identical.
    scarcityAdderPerMwh?: number;
    // v3.2 ADDITIVE: total FLAT nuclear output (baseload floor, incl. import nuclear). Constant
    // across ticks; the dashboards plot it as the flat band under the load-following gas. Optional.
    nuclearMw?: number;
    // v3.2 ADDITIVE: surplus baseload exported over the interface (Ontario net-export) — what keeps
    // nuclear flat at low demand instead of curtailing it. Optional.
    exportMw?: number;
    // ── v4 ADDITIVE (advanced dispatch, design §F) ──────────────────────────────────────────────
    // Marginal (price-setting) unit identity — the priciest unit with output>0 that cleared λ.
    marginalUnitId?: GenId;
    marginalFuel?: Fuel;
    marginalNetworkRole?: string;
    // Per-role generation stack bands (MW) — the dashboards plot these; golden asserts their shape
    // (nuclear/baseload ≈ flat, load_following tracks demand, peaker = 0 off-peak). storageNetMw is
    // signed: >0 discharging, <0 charging.
    baseloadMw?: number;
    loadFollowingMw?: number;
    peakerMw?: number;
    storageNetMw?: number;
    vreMw?: number;
    // engine.v3 P10: reservoir_hydro output band (water-value-arbitraging hydro), partitioned OUT of baseload so
    // the role bands still sum to genMw. 0 on the legacy/golden fleet (no reservoir units) ⇒ partition unchanged.
    hydroReservoirDischargeMw?: number;
    // Recovered DC marginal transmission loss (MW) — generation now exceeds load by losses.
    lossMw?: number;
    // engine.v3 P11 (C31): distribution / last-mile loss (MW) — the I²R copper loss in the LV feeder below B′,
    // modeled as a per-load adder and withdrawn at the load bus (in-B′, conservation-safe). Always-emitted 0 when
    // no loads carry distLossCoef (golden-neutral). totalTndLossMw = lossMw + distLossMw (the combined T&D total).
    distLossMw?: number;
    totalTndLossMw?: number;
    // CO₂ this tick = Σ output·co2KgPerMwh·TICK_HOURS (kg).
    emissionsKg?: number;
    // ── engine.v2 ADDITIVE (System-Dynamics frequency core, design §2,§4) ──────────────────────────
    // Islanded-COI STUDY frequency of the Ontario synchronous subset (NOT the live, Eastern-
    // Interconnection-tied frequency — design §1). 60.000 at steady state; dips after a lost infeed.
    frequencyHz?: number;
    // Rate of change of COI frequency dΔf/dt (Hz/s). 0 at steady state; <0 just after a generation trip.
    rocofHzPerS?: number;
    // System stored kinetic energy Σ(Hᵢ·Sᵢ) (MW·s) of the committed synchronous fleet this tick.
    // FALLS as VRE displaces synchronous units ⇒ the RoCoF/nadir worsen (design loop R1).
    inertiaMwS?: number;
    // System inertia constant H_sys = inertiaMwS / S_base (s). Aggregate fleet H, typ. ~4 s.
    inertiaConstantS?: number;
    // Area Control Error (MW), NERC ACE = −10·B·Δf; its integral drives AGC. 0 at steady state.
    aceMw?: number;
    // Frequency peak-excursion (Hz): grid-sampled sample furthest from 60 — nadir (min) for an
    // under-frequency event, zenith (max>60) for over-frequency. = 60 at steady state. Dip depth =
    // |60 − freqNadirHz|.
    freqNadirHz?: number;
    // Total primary-reserve headroom currently responding to droop (MW) — emergent from per-unit
    // governor headroom; small/zero in high-VRE, units-near-max hours (design §4.2 clamp).
    primaryReserveMw?: number;
    // ── engine.v2 P2 ADDITIVE (BESS fast-frequency-response + reservoir) ──
    // Peak BESS FFR power deployed during the transient (MW; >0 = discharge to arrest the nadir).
    // 0 at steady state; the BESS-compensates-for-lost-inertia counterpart to the R1 loop.
    bessFfrMw?: number;
    // Aggregate BESS reservoir state-of-charge this tick (MWh) across the online fleet.
    bessSocMwh?: number;
    // F6.2: the BESS √η round-trip CONVERSION loss metered this tick (MW; == MWh at 1 h/tick). The charge-side
    // (grid MW − stored) + discharge-side (drawn − delivered) + FFR √η energy that was applied to SoC but
    // previously DISCARDED. Always-emitted; 0 at golden (BATTERY rows carry no storage attrs ⇒ inert).
    bessConversionLossMw?: number;
    // engine.v3 P10 (hydro reservoir + water value) — telemetry, always-emitted when reservoir hydro exists (else
    // undefined ⇒ legacy/golden neutral). hydroReservoirMwh = aggregate reservoir SoC (mirrors bessSocMwh);
    // hydroWaterValuePerMwh = the fleet marginal water value ($/MWh) this tick (rises as the reservoirs draw down).
    hydroReservoirMwh?: number;
    hydroWaterValuePerMwh?: number;
    // engine.v3 P10-rewrite (2026-07-04, D-SPILL) — MW-equivalent water spilled this tick (inflow that overflowed
    // a full reservoir). Telemetry ONLY (NOT a power-balance term — the injection vector reads outputMw; spilled
    // water leaves the reservoir without generating). Always-emitted 0 when no reservoir units exist.
    hydroSpillMw?: number;
    // ── engine.v2 P3 ADDITIVE (price-elastic demand) ──
    // Demand response applied this tick: MW shifted off the consumer base (>0 = curtailed at high λ,
    // <0 = rebound off-peak). The price→demand→λ balancing loop. 0 when no flexible unit prices λ.
    demandResponseMw?: number;
    // The signed demand-response fraction that shaped this tick's consumer demand (−0.15…+0.15).
    demandResponseFrac?: number;
    // ── engine.v2 P4 ADDITIVE (finance + comms on the SD clock) ──
    // Information-layer health (0 during a comms fault → 1 fully recovered); scales info-flow throughput.
    commsHealthFrac?: number;
    // Finance settlement this tick ($, the flow) + the running cumulative settlement since init (the stock).
    settlementDollars?: number;
    cumulativeSettlementDollars?: number;
    // ── engine.v3 P15 ADDITIVE (L4 TOU-shift + L5 UFLS attribution) ────────────────────────────
    // L4: the retail-TOU load-shift reservoir level (MWh deferred out of on-peak, released into off-peak).
    // Always-emit 0 when empty (golden fixture: loads carry no shiftableFrac ⇒ no shift ⇒ 0 ⇒ byte-identical).
    deferredMwh?: number;
    // L5: the Hz-ladder-attributed UFLS shed THIS tick (MW), DISTINCT from the reserve-ramp shed already in
    // unservedMw. Attribution only — the ladder shed folds into the merged shedMap (P14 bills on served via it),
    // NOT a second subtraction. Always-emit 0 at N-0 (nadir ~60.000 ⇒ ladder contributes 0 ⇒ byte-identical).
    uflsShedMw?: number;
    // ── engine.v3 P0-harness ADDITIVE (the physics self-check, R2) ──────────────────────────────
    // The DERIVED DC reference-bus residual = −Σ_{i≠slack} P[i] (MW) — the true MW the slack sources/
    // sinks for KCL. Distinct from `slackMw` (the dispatched slack-plant output). A ballooning value is
    // the #1 tell of a conservation bug — now it's visible. Optional; older snapshots omit it.
    slackAbsorbedMw?: number;
    // The per-tick KCL conservation residual Σ P[i] over ALL buses (must be ~0). ≈0 on the golden
    // fixtures; the scalar-accounting leak (prosumer/BESS) makes it nonzero on the live graph until P1
    // closes it. Optional read-only diagnostic — surfacing it does not change dispatch/θ (golden-neutral).
    conservationResidualMw?: number;
    // ── domain-v5 ADDITIVE (engine.v3 P2 — conservation harness) ──────────────────────────────
    // The full per-tick physics self-check (conservation / island balance / hub KCL / capacity /
    // stocks / money). ok:true + empty violations on every N-0 tick. Optional; older snapshots omit it.
    invariant?: InvariantReport;
    // ── domain-v6 ADDITIVE (engine.v3 P5 — SCED / binding capacity) ─────────────────────────────
    // The live Most Severe Single Contingency = the biggest single dispatched-unit output (MW). Sizes the
    // operating-reserve requirement, which now floats above the static floor instead of a flat 1400.
    // Always emitted by the P5 engine (0 when nothing dispatches); older snapshots omit it.
    largestContingencyMw?: number;
    // P5 diagnostics (optional UI readouts): import corridors that hit their per-corridor ATC this tick, and
    // the count of closed branches still over their continuous rating (the weak-corridor DATA overloads the
    // SCED surfaces but — scoped — does not shed). Empty/0 on the golden fixture (no caps / no rated edges).
    bindingCorridors?: CorridorId[];
    bindingBranchCount?: number;
    // ── domain-v7 ADDITIVE (engine.v3 P7 — LinDistFlow voltage) ─────────────────────────────────
    // Min / max per-bus vMagPu = √w over ENERGIZED, non-displayOnly buses this tick, and the count below the
    // ANSI C84.1 Range A service floor (0.95 pu). ≈1.0/1.0/0 on the neutral path; under a heatwave / heavy
    // downstream load, minVMagPu drops and sagBusCount rises. Always emitted by the P7 engine; undefined ⇒
    // the engine ran WITHOUT the voltage layer this tick (older snapshot). See docs/interfaces/domain.v7.md.
    minVMagPu?: number;
    maxVMagPu?: number;
    sagBusCount?: number;
    // engine.v3 P8 (env→infrastructure coupling) — telemetry, ALWAYS emitted (neutral 1.0 / 0 at/below the
    // references). A temperature dial now moves these: lineRatingFactor (IEEE-738 DLR ≤1) shrinks line ampacity
    // in heat; resistanceFactor (ACSR R(T) ≥1) raises I²R loss + voltage sag; gasDerateMw is the combustion-
    // turbine MW the heat shaved off the merit stack; ironLossMw is the CONSERVED transformer no-load draw.
    lineRatingFactor?: number;
    resistanceFactor?: number;
    gasDerateMw?: number;
    ironLossMw?: number;
    // engine.v3 P9 (temporal dynamics / unit-commitment) — telemetry, ALWAYS emitted (0 / count on the neutral
    // path). startupCostDollars/noLoadCostDollars are the UC economic costs THIS tick (one-time start on the
    // decommitted→committed transition; no-load billed each committed tick incl. at 0 output) surfaced as
    // diagnostics — NOT settlement legs (avoids the |Σpay−Σreceive| money-identity risk, like ironLossMw).
    // committedUnits = # currently-committed generators; rampBoundUnits = # whose up-ramp band bound this tick.
    startupCostDollars?: number;
    noLoadCostDollars?: number;
    committedUnits?: number;
    rampBoundUnits?: number;
    // engine.v3 P9-rewrite (2026-07-03, H1 liveness observability) — telemetry, ALWAYS emitted (0 on the neutral
    // path). # units whose commitDecision returned "decommit" but the reserve check (canDecommit) vetoed — i.e.
    // units that ARE uneconomic at their floor but CAN'T shut because their MW is needed. A persistent non-zero
    // count signals the fleet is over-committed relative to demand (units burning no-load cost they can't recover).
    decommitLatchedUnits?: number;
    // engine.v3 P12 (C22): protection coordination — # branches locked out (C24 reclose exhausted) + #
    // branches that wanted to trip but had NO switchable breaker in their zone (C15 honest limitation).
    // Always-emitted 0 when no contingency is active (golden-neutral).
    lockedOutCount?: number;
    protectionUntripCount?: number;
    // ── engine.v3 P16 ADDITIVE (visual-ladder reconciliation + wire propagation) ──────────────────
    // C67: the honest supply margin = genMw − netLoadMw − lossMw − exportMw (NOT genMw−netLoadMw which
    // conflates loss+export). Always-emitted; on golden ticks it's an exact renaming of existing arithmetic.
    supplyMarginMw?: number;
    // C68: the "other" generation band computed ONCE = Math.max(0, genMw − nuclear − gas − import). Single
    // clamp source so the chart (series.ts) and the headline (panels.ts) never diverge. Always-emitted.
    otherGenMw?: number;
    // C84: true iff THIS tick has a live frequency transient (rocof≠0 or nadir≠60 or an active fault). Dashboards
    // plot nadir/RoCoF only when true; hold the steady baseline (60/0) otherwise. Always-emitted false on N-0.
    isDisturbance?: boolean;
  };
  flows: readonly FlowSample[]; // all three layers
  outages: readonly NodeId[]; // de-energized nodes (node-level; LoadRuntime.status is load-level)
  // P4 (ontario-merge) ADDITIVE: nodes flagged displayOnly (un-bridgeable out-of-GTA OSM fragments rendered as
  // static infrastructure, never in the solve). Distinct from `outages` (which are SOLVED-but-de-energized
  // buses). The UI uses this to label a displayOnly pick honestly ("static infrastructure — no live feed")
  // instead of offering a no-op Turn-off. Optional; older snapshots omit it.
  displayOnlyNodes?: readonly NodeId[];
  // P-viz fix (additive): lines the operator manually took OUT OF SERVICE (toggleEdge). The UI uses this to
  // label a line pick "Restore line" vs "Turn off line". Distinct from faultsActive (a fault) and from
  // de-energized-by-upstream-outage. Optional; older snapshots omit it.
  manualOffEdges?: readonly EdgeId[];
  // REV-B (build log 77, additive): per-CONNECTION breakers — ports the operator has opened (or that the data
  // ships open via uClosed/vClosed, the V3 connections_map per-connection switch). A port is one END of an edge:
  // encoded "<edgeId>|u" or "<edgeId>|v". An edge with EITHER port open carries no flow (isolated from that
  // bus). The UI renders a connection-point marker per port and labels an open one "disconnected". Optional;
  // older snapshots omit it (⇔ all connections closed). Distinct from manualOffEdges (whole-line outage).
  openPorts?: readonly string[];
  faultsActive: readonly Fault[];
  prosumers?: readonly ProsumerRuntime[]; // NEW in v2 — per-prosumer runtime (optional)
  loads?: readonly LoadRuntime[]; // NEW in v2 — per-load service state (optional)
  // P13 (KS-PRICE legibility) ADDITIVE: the cost-sorted merit/supply stack (see MeritStackEntry) — drives the
  // delivered-cost supply-curve panel + names the marginal plant. Optional; older snapshots omit it.
  meritStack?: readonly MeritStackEntry[];
  // engine.v3 P13 (C08): per-bus nodal LMP decomposition (energy + congestion + loss). GATED — absent on
  // merit-default N-0 ticks (byte-identical); present only when mode==="dlmp" or bindingDuals.length>0.
  nodalPrices?: readonly { node: NodeId; lmpPerMwh: number; energyPerMwh: number; congestionPerMwh: number; lossPerMwh: number }[];
}

// ───────────────────────── Commands (state/UI → worker) ──────────────────────
export type Command =
  | { t: "setClock"; hour: number; speed: number }
  | { t: "setScenario"; scenarioId: string }
  // v3 additive: live environment overrides (hour rides setClock) — the UI's season/temperature
  // controls. Only the provided fields override the active scenario; setScenario clears them.
  | { t: "setEnv"; season?: Season; dayType?: DayType; tempC?: number }
  | { t: "toggleNode"; id: NodeId; on: boolean }
  // P-viz fix: planned line outage — take a transmission line out of service / restore it (distinct from a
  // FAULT: no FLISR self-heal trigger, just open/close the branch). `on:false` opens it, `on:true` restores.
  | { t: "toggleEdge"; id: EdgeId; on: boolean }
  // REV-B: per-CONNECTION breaker — open/close ONE END of an edge (its connection to that facility's bus),
  // like a connections_map powerPort. `closed:false` opens the port (the edge then carries no flow, isolated
  // from that bus); `closed:true` restores it. Distinct from toggleEdge (which opens the whole line).
  | { t: "togglePort"; id: EdgeId; end: "u" | "v"; closed: boolean }
  | { t: "injectFault"; fault: Fault }
  | { t: "clearFault"; fault: Fault }
  | { t: "setLayerVisible"; layer: Layer; visible: boolean };
